import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Chip, Checkbox,
  CircularProgress, IconButton, LinearProgress,
  Menu, MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getStigSummary } from '../api/stig';
import { uploadCkl } from '../api/stigMgmt';

// ── Column filter (reused from StigCompliance) ──────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }}
                sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

const CAT_OPTS = ['Has Findings', 'None'];

function catCheck(f, val) {
  if (f.length === 0) return true;
  return (f.includes('Has Findings') && val > 0) || (f.includes('None') && val === 0);
}

// ── Main Dialog ─────────────────────────────────────────────────

export default function BulkCklUploadDialog({ open, onClose, systemId }) {
  const [step, setStep]       = useState(0); // 0=upload+filter, 1=results
  const [devices, setDevices] = useState([]);
  const [cklFile, setCklFile] = useState(null);

  // Filters
  const [filterOS,    setFilterOS]    = useState([]);
  const [filterType,  setFilterType]  = useState([]);
  const [filterCatI,  setFilterCatI]  = useState([]);
  const [filterCatII, setFilterCatII] = useState([]);
  const [filterCatIII,setFilterCatIII]= useState([]);
  const [filterOpen,  setFilterOpen]  = useState([]);
  const [filterNAF,   setFilterNAF]   = useState([]);
  const [filterNA,    setFilterNA]    = useState([]);
  const [filterNR,    setFilterNR]    = useState([]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [applying, setApplying]       = useState(false);
  const [results, setResults]         = useState([]);
  const [progress, setProgress]       = useState(0);

  useEffect(() => {
    if (open) {
      getStigSummary(systemId).then(setDevices).catch(console.error);
      setCklFile(null);
      setSelectedIds(new Set());
      setResults([]);
      setStep(0);
      setProgress(0);
      setFilterOS([]); setFilterType([]);
      setFilterCatI([]); setFilterCatII([]); setFilterCatIII([]);
      setFilterOpen([]); setFilterNAF([]); setFilterNA([]); setFilterNR([]);
    }
  }, [open]);

  const osOptions   = [...new Set(devices.map(d => d.os).filter(Boolean))].sort();
  const typeOptions = [...new Set(devices.map(d => d.type).filter(Boolean))].sort();

  const filtered = devices.filter(d => {
    if (filterOS.length > 0 && !filterOS.includes(d.os)) return false;
    if (filterType.length > 0 && !filterType.includes(d.type)) return false;
    if (!catCheck(filterCatI,   d.catI))         return false;
    if (!catCheck(filterCatII,  d.catII))         return false;
    if (!catCheck(filterCatIII, d.catIII))        return false;
    if (!catCheck(filterOpen,   d.open || 0))     return false;
    if (!catCheck(filterNAF,    d.notAFinding || 0)) return false;
    if (!catCheck(filterNA,     d.notApplicable || 0)) return false;
    if (!catCheck(filterNR,     d.notReviewed || 0))   return false;
    return true;
  });

  const allSelected = filtered.length > 0 && filtered.every(d => selectedIds.has(d.id));
  const someSelected = filtered.some(d => selectedIds.has(d.id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(prev => { const n = new Set(prev); filtered.forEach(d => n.delete(d.id)); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); filtered.forEach(d => n.add(d.id)); return n; });
    }
  };

  const toggleOne = useCallback((id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const handleApply = async () => {
    if (!cklFile || selectedIds.size === 0) return;
    setApplying(true);
    setStep(1);
    setProgress(0);

    const ids = [...selectedIds];
    const out = [];
    for (let i = 0; i < ids.length; i++) {
      const assetId = ids[i];
      const device  = devices.find(d => d.id === assetId);
      try {
        const res = await uploadCkl(assetId, systemId, cklFile);
        out.push({ hostname: device?.hostname, status: 'ok', checks: res.checks_upserted ?? res.total ?? '—', message: res.message });
      } catch (e) {
        out.push({ hostname: device?.hostname, status: 'error', message: e.message });
      }
      setResults([...out]);
      setProgress(Math.round(((i + 1) / ids.length) * 100));
    }
    setApplying(false);
  };

  const totalFilters = filterOS.length + filterType.length + filterCatI.length + filterCatII.length +
    filterCatIII.length + filterOpen.length + filterNAF.length + filterNA.length + filterNR.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', height: '85vh', display: 'flex', flexDirection: 'column' } }}>

      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
        <Box>
          <Typography sx={{ color: '#ffffff', fontWeight: 700 }}>Bulk CKL Upload</Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>
            {step === 0
              ? `Upload a checklist and select target devices — ${selectedIds.size} selected`
              : `Applying to ${[...selectedIds].length} device${[...selectedIds].length !== 1 ? 's' : ''}…`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
      </DialogTitle>

      {applying && (
        <LinearProgress variant="determinate" value={progress}
          sx={{ backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: '#00bcd4' } }} />
      )}

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>

        {/* STEP 0: Upload + Filter + Select */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

            {/* CKL file upload bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '12px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small"
                sx={{ borderColor: '#00bcd4', color: '#00bcd4', '&:hover': { backgroundColor: '#00bcd411' }, whiteSpace: 'nowrap' }}>
                {cklFile ? 'Change File' : 'Select .ckl File'}
                <input type="file" accept=".ckl" hidden onChange={e => setCklFile(e.target.files[0] || null)} />
              </Button>
              {cklFile ? (
                <Typography sx={{ color: '#4caf50', fontSize: 13, fontFamily: 'monospace' }}>{cklFile.name}</Typography>
              ) : (
                <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>No file selected — choose a STIG Viewer checklist (.ckl)</Typography>
              )}
            </Box>

            {/* Filter header row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '32px 160px 1fr 60px 60px 70px 80px 110px 110px 110px', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #1e3a5f', backgroundColor: '#0a0e1a' }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                size="small"
                sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' }, '&.MuiCheckbox-indeterminate': { color: '#00bcd4' }, p: 0 }}
              />
              <ColFilter label="Device"        options={[]}         value={[]} onChange={() => {}} />
              <ColFilter label="OS"            options={osOptions}  value={filterOS}    onChange={setFilterOS} />
              <ColFilter label="CAT I"         options={CAT_OPTS}   value={filterCatI}  onChange={setFilterCatI} />
              <ColFilter label="CAT II"        options={CAT_OPTS}   value={filterCatII} onChange={setFilterCatII} />
              <ColFilter label="CAT III"       options={CAT_OPTS}   value={filterCatIII} onChange={setFilterCatIII} />
              <ColFilter label="Open"          options={CAT_OPTS}   value={filterOpen}  onChange={setFilterOpen} />
              <ColFilter label="Not a Finding" options={CAT_OPTS}   value={filterNAF}   onChange={setFilterNAF} />
              <ColFilter label="Not Applicable" options={CAT_OPTS}  value={filterNA}    onChange={setFilterNA} />
              <ColFilter label="Not Reviewed"  options={CAT_OPTS}   value={filterNR}    onChange={setFilterNR} />
            </Box>

            {/* Type filter row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #1e3a5f', backgroundColor: '#0a0e1a' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 11, textTransform: 'uppercase', mr: 1 }}>Type:</Typography>
              {typeOptions.map(t => {
                const sel = filterType.includes(t);
                return (
                  <Chip key={t} label={t} size="small" onClick={() => setFilterType(prev => sel ? prev.filter(x => x !== t) : [...prev, t])}
                    sx={{ backgroundColor: sel ? '#00bcd422' : '#1e3a5f', color: sel ? '#00bcd4' : '#7a8fa6', fontWeight: sel ? 700 : 400, fontSize: 11, cursor: 'pointer', height: 22 }} />
                );
              })}
              {totalFilters > 0 && (
                <Button size="small" onClick={() => { setFilterOS([]); setFilterType([]); setFilterCatI([]); setFilterCatII([]); setFilterCatIII([]); setFilterOpen([]); setFilterNAF([]); setFilterNA([]); setFilterNR([]); }}
                  sx={{ color: '#f44336', fontSize: 11, ml: 'auto', minWidth: 0 }}>
                  Clear all filters
                </Button>
              )}
            </Box>

            {/* Device list */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <Typography sx={{ color: '#4a5a70', fontSize: 13, textAlign: 'center', mt: 4 }}>No devices match the current filters</Typography>
              ) : filtered.map(d => {
                const checked = selectedIds.has(d.id);
                const catIColor  = d.catI > 0 ? '#f44336' : '#4a5a70';
                const catIIColor = d.catII > 0 ? '#ff9800' : '#4a5a70';
                const catIIIColor= d.catIII > 0 ? '#ffeb3b' : '#4a5a70';
                return (
                  <Box key={d.id} onClick={() => toggleOne(d.id)}
                    sx={{ display: 'grid', gridTemplateColumns: '32px 160px 1fr 60px 60px 70px 80px 110px 110px 110px',
                      alignItems: 'center', gap: 1, px: 2, py: 1,
                      borderBottom: '1px solid #0d1825',
                      backgroundColor: checked ? '#0d2137' : 'transparent',
                      cursor: 'pointer', '&:hover': { backgroundColor: '#0d1f33' } }}>
                    <Checkbox checked={checked} size="small"
                      sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#00bcd4' }, p: 0 }} onClick={e => e.stopPropagation()} onChange={() => toggleOne(d.id)} />
                    <Typography sx={{ color: '#ffffff', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.hostname}</Typography>
                    <Typography sx={{ color: '#7a8fa6', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.os}</Typography>
                    <Typography sx={{ color: catIColor,   fontWeight: 700, fontSize: 13 }}>{d.catI}</Typography>
                    <Typography sx={{ color: catIIColor,  fontWeight: 700, fontSize: 13 }}>{d.catII}</Typography>
                    <Typography sx={{ color: catIIIColor, fontWeight: 700, fontSize: 13 }}>{d.catIII}</Typography>
                    <Typography sx={{ color: (d.open || 0) > 0 ? '#f44336' : '#4a5a70', fontWeight: 700, fontSize: 13 }}>{d.open || 0}</Typography>
                    <Typography sx={{ color: (d.notAFinding || 0) > 0 ? '#4caf50' : '#4a5a70', fontWeight: 700, fontSize: 13 }}>{d.notAFinding || 0}</Typography>
                    <Typography sx={{ color: (d.notApplicable || 0) > 0 ? '#00bcd4' : '#4a5a70', fontWeight: 700, fontSize: 13 }}>{d.notApplicable || 0}</Typography>
                    <Typography sx={{ color: (d.notReviewed || 0) > 0 ? '#7a8fa6' : '#4a5a70', fontWeight: 700, fontSize: 13 }}>{d.notReviewed || 0}</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ p: '8px 16px', borderTop: '1px solid #1e3a5f', backgroundColor: '#0a0e1a' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>
                {filtered.length} device{filtered.length !== 1 ? 's' : ''} shown
                {totalFilters > 0 ? ` · ${totalFilters} filter${totalFilters !== 1 ? 's' : ''} active` : ''}
                {selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''}
              </Typography>
            </Box>
          </Box>
        )}

        {/* STEP 1: Results */}
        {step === 1 && (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
            {results.length === 0 && applying && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress sx={{ color: '#00bcd4' }} />
              </Box>
            )}
            {results.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: '1px solid #0d1825' }}>
                {r.status === 'ok'
                  ? <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, flexShrink: 0 }} />
                  : <ErrorIcon sx={{ color: '#f44336', fontSize: 18, flexShrink: 0 }} />}
                <Typography sx={{ color: '#ffffff', fontFamily: 'monospace', fontSize: 13, fontWeight: 600, minWidth: 140 }}>{r.hostname}</Typography>
                <Typography sx={{ color: r.status === 'ok' ? '#7a8fa6' : '#f44336', fontSize: 12 }}>
                  {r.status === 'ok' ? `${r.checks} checks updated` : r.message}
                </Typography>
              </Box>
            ))}
            {!applying && results.length > 0 && (
              <Box sx={{ p: '12px 16px', borderTop: '1px solid #1e3a5f', backgroundColor: '#0a0e1a' }}>
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>
                  {results.filter(r => r.status === 'ok').length} succeeded · {results.filter(r => r.status === 'error').length} failed
                </Typography>
              </Box>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e3a5f', p: 1.5, gap: 1 }}>
        {step === 0 && (
          <>
            <Button onClick={onClose} sx={{ color: '#7a8fa6' }}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={handleApply}
              disabled={!cklFile || selectedIds.size === 0}
              sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700 }}>
              Upload to {selectedIds.size} Device{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </>
        )}
        {step === 1 && !applying && (
          <Button variant="contained" onClick={onClose}
            sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700 }}>
            Done
          </Button>
        )}
        {step === 1 && applying && (
          <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>
            Processing… {results.length} / {selectedIds.size}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
}
